/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

export const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const normalizeResult = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

export const getId = (competitor) => {
  const id = Number(competitor?.id);
  return Number.isFinite(id) ? id : null;
};

export const isRealCompetitor = (competitor) =>
  !!competitor && !competitor.pending && getId(competitor) !== null;

export const getFullName = (competitor) => {
  if (!competitor) return "";
  if (competitor.pending) return "En attente";

  const nom = competitor.nom_competiteur || "";
  const prenom = competitor.prenom_competiteur || "";

  return `${nom} ${prenom}`.trim() || "Combattant";
};

export const getClubName = (competitor) => {
  if (!competitor || competitor.pending) return "";
  return competitor.club_competiteur || competitor.club || "";
};

export const makePending = (label = "En attente de combat") => ({
  pending: true,
  label,
});

/* -------------------------------------------------------------------------- */
/* Positions uniques                                                           */
/* -------------------------------------------------------------------------- */

const PHASE_OFFSET = {
  MAIN: 1000,
  REP_A: 3000,
  REP_B: 4000,
  BRONZE_A: 5000,
  BRONZE_B: 5100,
};

export const getMatchPositions = (phase, roundIndex = 0, matchIndex = 0) => {
  const base = PHASE_OFFSET[phase] || 9000;
  const start = base + roundIndex * 100 + matchIndex * 2;

  return {
    position1: start + 1,
    position2: start + 2,
  };
};

const readCombatPosition1 = (combat) => {
  const value =
    combat.position1 ??
    combat.position_1 ??
    combat.position_competiteur1 ??
    combat.position_competiteur_1 ??
    combat.position_c1;

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const readCombatPosition2 = (combat) => {
  const value =
    combat.position2 ??
    combat.position_2 ??
    combat.position_competiteur2 ??
    combat.position_competiteur_2 ??
    combat.position_c2;

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/* -------------------------------------------------------------------------- */
/* Combat helpers                                                              */
/* -------------------------------------------------------------------------- */

export const findCombatByIds = (combats = [], id1, id2) => {
  const a = Number(id1);
  const b = Number(id2);

  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

  return (
    combats.find((combat) => {
      const c1 = Number(combat.competiteur_id1);
      const c2 = Number(combat.competiteur_id2);

      return (c1 === a && c2 === b) || (c1 === b && c2 === a);
    }) || null
  );
};

export const findCombatForMatch = ({
  combats = [],
  c1,
  c2,
  position1,
  position2,
}) => {
  if (!isRealCompetitor(c1) || !isRealCompetitor(c2)) return null;

  const p1 = Number(position1);
  const p2 = Number(position2);

  if (Number.isFinite(p1) && Number.isFinite(p2)) {
    const byPositions = combats.find((combat) => {
      const cp1 = readCombatPosition1(combat);
      const cp2 = readCombatPosition2(combat);

      if (cp1 === null || cp2 === null) return false;

      return (cp1 === p1 && cp2 === p2) || (cp1 === p2 && cp2 === p1);
    });

    if (byPositions) return byPositions;
  }

  return findCombatByIds(combats, getId(c1), getId(c2));
};

export const getWinnerIdFromCombat = (combat) => {
  if (!combat) return null;

  const id1 = Number(combat.competiteur_id1);
  const id2 = Number(combat.competiteur_id2);

  if (!Number.isFinite(id1) || !Number.isFinite(id2)) return null;

  const r1 = normalizeResult(combat.resultat_competiteur1);
  const r2 = normalizeResult(combat.resultat_competiteur2);

  if (r1 === "V") return id1;
  if (r2 === "V") return id2;

  if (["D", "F", "H"].includes(r1) && !["D", "F", "H"].includes(r2)) {
    return id2;
  }

  if (["D", "F", "H"].includes(r2) && !["D", "F", "H"].includes(r1)) {
    return id1;
  }

  const p1 =
    combat.points_competiteur1 !== null &&
    combat.points_competiteur1 !== undefined
      ? Number(combat.points_competiteur1)
      : null;

  const p2 =
    combat.points_competiteur2 !== null &&
    combat.points_competiteur2 !== undefined
      ? Number(combat.points_competiteur2)
      : null;

  if (Number.isFinite(p1) && Number.isFinite(p2)) {
    if (p1 > p2) return id1;
    if (p2 > p1) return id2;
  }

  return null;
};

export const getResultForCompetitor = (combat, competitor) => {
  if (!combat || !competitor) {
    return { result: "", points: null };
  }

  const id = getId(competitor);
  if (!id) {
    return { result: "", points: null };
  }

  const id1 = Number(combat.competiteur_id1);
  const id2 = Number(combat.competiteur_id2);

  if (id === id1) {
    return {
      result: normalizeResult(combat.resultat_competiteur1),
      points: combat.points_competiteur1,
    };
  }

  if (id === id2) {
    return {
      result: normalizeResult(combat.resultat_competiteur2),
      points: combat.points_competiteur2,
    };
  }

  return { result: "", points: null };
};

export const getLoserFromMatch = (match) => {
  if (!match || !match.combat) return null;

  const winnerId = getWinnerIdFromCombat(match.combat);
  if (!winnerId) return null;

  const id1 = getId(match.competiteur1);
  const id2 = getId(match.competiteur2);

  if (id1 === winnerId) return match.competiteur2 || null;
  if (id2 === winnerId) return match.competiteur1 || null;

  return null;
};

/* -------------------------------------------------------------------------- */
/* Main bracket                                                                */
/* -------------------------------------------------------------------------- */

const makeMainPairs = (list = [], roundIndex = 0, combats = []) => {
  const pairs = [];

  for (let i = 0; i < list.length; i += 2) {
    const matchIndex = i / 2;
    const positions = getMatchPositions("MAIN", roundIndex, matchIndex);

    const c1 = list[i] || null;
    const c2 = list[i + 1] || null;

    const combat = findCombatForMatch({
      combats,
      c1,
      c2,
      position1: positions.position1,
      position2: positions.position2,
    });

    const winnerId = getWinnerIdFromCombat(combat);

    const autoWinner =
      isRealCompetitor(c1) && !c2
        ? c1
        : !c1 && isRealCompetitor(c2)
        ? c2
        : null;

    pairs.push({
      phase: "MAIN",
      roundIndex,
      matchIndex,
      competiteur1: c1,
      competiteur2: c2,
      position1: positions.position1,
      position2: positions.position2,
      combat,
      winnerId,
      winner: null,
      autoWinner,
    });
  }

  return pairs;
};

const getGeneratedRoundTitle = (baseSize, roundIndex) => {
  const matchCount = Math.max(1, baseSize / Math.pow(2, roundIndex + 1));

  if (matchCount === 1) return "Finale";
  if (matchCount === 2) return "1/2 finale";
  if (matchCount === 4) return "1/4 finale";
  if (matchCount === 8) return "1/8 finale";
  if (matchCount === 16) return "1/16 finale";
  if (matchCount === 32) return "1/32 finale";

  return `1/${matchCount} finale`;
};

const getRoundSubtitle = (matchCount) => {
  if (matchCount === 1) return "Vainqueur";
  return `${matchCount * 2} combattants`;
};

export const buildMainRoundsForSystematicRepechage = ({
  seed = [],
  competiteurs = [],
  combats = [],
  roundNames = [],
}) => {
  const startingSeed = Array.isArray(seed) && seed.length ? seed : competiteurs;
  const baseSize = Math.max(2, startingSeed.length);

  const byId = new Map(
    (competiteurs || [])
      .filter((competitor) => getId(competitor) !== null)
      .map((competitor) => [getId(competitor), competitor]),
  );

  const rounds = [];
  let current = startingSeed;
  let roundIndex = 0;

  while (current.length > 1 && roundIndex < 12) {
    const pairs = makeMainPairs(current, roundIndex, combats).map((pair) => {
      const winner =
        pair.winnerId && byId.has(pair.winnerId)
          ? byId.get(pair.winnerId)
          : null;

      return {
        ...pair,
        winner,
      };
    });

    rounds.push({
      roundIndex,
      title:
        roundNames?.[roundIndex] ||
        getGeneratedRoundTitle(baseSize, roundIndex),
      subtitle: getRoundSubtitle(pairs.length),
      matchCount: pairs.length,
      pairs,
    });

    const nextRound = pairs.map((pair) => {
      if (pair.autoWinner) return pair.autoWinner;

      if (pair.winnerId && byId.has(pair.winnerId)) {
        return byId.get(pair.winnerId);
      }

      if (
        isRealCompetitor(pair.competiteur1) &&
        isRealCompetitor(pair.competiteur2)
      ) {
        return makePending(`Vainqueur ${pair.phase}-${roundIndex}-${pair.matchIndex}`);
      }

      return makePending(`Vainqueur ${pair.phase}-${roundIndex}-${pair.matchIndex}`);
    });

    current = nextRound;
    roundIndex += 1;
  }

  return rounds;
};

const getMainMatch = (mainRounds, roundIndex, matchIndex) => {
  return mainRounds?.[roundIndex]?.pairs?.[matchIndex] || null;
};

const getMainLoserCandidate = (mainRounds, roundIndex, matchIndex) => {
  const match = getMainMatch(mainRounds, roundIndex, matchIndex);

  if (!match) return makePending("Perdant à déterminer");

  if (match.autoWinner) return null;

  if (
    isRealCompetitor(match.competiteur1) &&
    isRealCompetitor(match.competiteur2) &&
    !match.combat
  ) {
    return makePending(`Perdant ${mainRounds[roundIndex]?.title || ""}`);
  }

  const loser = getLoserFromMatch(match);
  return loser || null;
};

/* -------------------------------------------------------------------------- */
/* Systematic repechage engine                                                 */
/* -------------------------------------------------------------------------- */

const createRepechageMatch = ({
  side,
  displayRoundIndex,
  displayOrder,
  label,
  c1,
  c2,
  source1 = null,
  source2 = null,
  combats,
}) => {
  const phase = side === "A" ? "REP_A" : "REP_B";
  const positions = getMatchPositions(phase, displayRoundIndex, displayOrder);

  const combat = findCombatForMatch({
    combats,
    c1,
    c2,
    position1: positions.position1,
    position2: positions.position2,
  });

  const winnerId = getWinnerIdFromCombat(combat);

  const autoWinner =
    isRealCompetitor(c1) && !c2
      ? c1
      : !c1 && isRealCompetitor(c2)
      ? c2
      : null;

  const winner =
    winnerId === getId(c1)
      ? c1
      : winnerId === getId(c2)
      ? c2
      : autoWinner || null;

  const key = `${phase}-R${displayRoundIndex}-M${displayOrder}`;

  return {
    key,
    phase,
    side,
    title: label,
    displayRoundIndex,
    displayOrder,
    roundIndex: displayRoundIndex,
    matchIndex: displayOrder,
    competiteur1: c1 || null,
    competiteur2: c2 || null,
    position1: positions.position1,
    position2: positions.position2,
    combat,
    winnerId,
    winner,
    autoWinner,
    source1,
    source2,
  };
};

const createBronzeMatch = ({
  side,
  c1,
  c2,
  source1 = null,
  source2 = null,
  combats,
}) => {
  const phase = side === "A" ? "BRONZE_A" : "BRONZE_B";
  const positions = getMatchPositions(phase, 0, 0);

  const combat = findCombatForMatch({
    combats,
    c1,
    c2,
    position1: positions.position1,
    position2: positions.position2,
  });

  const winnerId = getWinnerIdFromCombat(combat);

  const winner =
    winnerId === getId(c1)
      ? c1
      : winnerId === getId(c2)
      ? c2
      : null;

  return {
    key: phase,
    phase,
    side,
    title: side === "A" ? "Combat 3e place A" : "Combat 3e place B",
    competiteur1: c1 || makePending("Gagnant du repêchage"),
    competiteur2: c2 || makePending("Perdant de demi-finale"),
    position1: positions.position1,
    position2: positions.position2,
    combat,
    winnerId,
    winner,
    source1,
    source2,
  };
};

const getMatchOutput = (match, fallbackLabel = "Vainqueur à déterminer") => {
  if (match.winner) {
    return {
      competitor: match.winner,
      sourceKey: match.key,
    };
  }

  if (match.autoWinner) {
    return {
      competitor: match.autoWinner,
      sourceKey: match.key,
    };
  }

  return {
    competitor: makePending(fallbackLabel),
    sourceKey: match.key,
  };
};

const buildRepForMainSubtree = ({
  side,
  mainRounds,
  combats,
  roundIndex,
  matchIndex,
  matches,
}) => {
  if (roundIndex === 0) {
    const loser = getMainLoserCandidate(mainRounds, 0, matchIndex);

    return {
      competitor: loser || null,
      sourceKey: null,
    };
  }

  const left = buildRepForMainSubtree({
    side,
    mainRounds,
    combats,
    roundIndex: roundIndex - 1,
    matchIndex: matchIndex * 2,
    matches,
  });

  const right = buildRepForMainSubtree({
    side,
    mainRounds,
    combats,
    roundIndex: roundIndex - 1,
    matchIndex: matchIndex * 2 + 1,
    matches,
  });

  const combineLevel = Math.max(0, (roundIndex - 1) * 2);
  const combineOrder = matches.filter(
    (m) => m.displayRoundIndex === combineLevel,
  ).length;

  const combineMatch = createRepechageMatch({
    side,
    displayRoundIndex: combineLevel,
    displayOrder: combineOrder,
    label: `Repêchage ${side} — tour ${combineLevel + 1}`,
    c1: left.competitor,
    c2: right.competitor,
    source1: left.sourceKey,
    source2: right.sourceKey,
    combats,
  });

  matches.push(combineMatch);

  const combineOutput = getMatchOutput(
    combineMatch,
    "Vainqueur du repêchage à déterminer",
  );

  const mainLoser = getMainLoserCandidate(mainRounds, roundIndex, matchIndex);

  if (!mainLoser) {
    return combineOutput;
  }

  const entryLevel = combineLevel + 1;
  const entryOrder = matches.filter(
    (m) => m.displayRoundIndex === entryLevel,
  ).length;

  const entryMatch = createRepechageMatch({
    side,
    displayRoundIndex: entryLevel,
    displayOrder: entryOrder,
    label: `Repêchage ${side} — entrée perdant du tour principal`,
    c1: combineOutput.competitor,
    c2: mainLoser,
    source1: combineOutput.sourceKey,
    source2: null,
    combats,
  });

  matches.push(entryMatch);

  return getMatchOutput(entryMatch, "Qualifié repêchage à déterminer");
};

const buildRepechageSide = ({
  side,
  semiMatchIndex,
  semiRoundIndex,
  mainRounds,
  combats,
}) => {
  const matches = [];

  if (semiRoundIndex <= 0) {
    return {
      side,
      rounds: [],
      matches,
      qualifier: makePending("Repêchage indisponible"),
      qualifierSourceKey: null,
    };
  }

  const leftMainMatchIndex = semiMatchIndex * 2;
  const rightMainMatchIndex = semiMatchIndex * 2 + 1;

  const left = buildRepForMainSubtree({
    side,
    mainRounds,
    combats,
    roundIndex: semiRoundIndex - 1,
    matchIndex: leftMainMatchIndex,
    matches,
  });

  const right = buildRepForMainSubtree({
    side,
    mainRounds,
    combats,
    roundIndex: semiRoundIndex - 1,
    matchIndex: rightMainMatchIndex,
    matches,
  });

  const finalLevel = Math.max(0, (semiRoundIndex - 1) * 2);
  const finalOrder = matches.filter((m) => m.displayRoundIndex === finalLevel)
    .length;

  const finalMatch = createRepechageMatch({
    side,
    displayRoundIndex: finalLevel,
    displayOrder: finalOrder,
    label: `Finale repêchage ${side}`,
    c1: left.competitor,
    c2: right.competitor,
    source1: left.sourceKey,
    source2: right.sourceKey,
    combats,
  });

  matches.push(finalMatch);

  const finalOutput = getMatchOutput(
    finalMatch,
    `Gagnant du repêchage ${side}`,
  );

  const grouped = new Map();

  matches.forEach((match) => {
    if (!grouped.has(match.displayRoundIndex)) {
      grouped.set(match.displayRoundIndex, []);
    }
    grouped.get(match.displayRoundIndex).push(match);
  });

  const rounds = Array.from(grouped.entries())
    .sort(([a], [b]) => a - b)
    .map(([roundIndex, roundMatches]) => ({
      roundIndex,
      title:
        roundIndex === finalLevel
          ? `Finale repêchage ${side}`
          : `Repêchage ${side} — Tour ${roundIndex + 1}`,
      matches: roundMatches.map((match, index) => ({
        ...match,
        displayOrder: index,
      })),
    }));

  return {
    side,
    rounds,
    matches,
    qualifier: finalOutput.competitor,
    qualifierSourceKey: finalOutput.sourceKey,
  };
};

export const buildSystematicRepechageData = ({
  mainRounds = [],
  combats = [],
}) => {
  const semiRoundIndex = mainRounds.length - 2;
  const semiRound = mainRounds[semiRoundIndex] || null;

  const semiA = semiRound?.pairs?.[0] || null;
  const semiB = semiRound?.pairs?.[1] || null;

  const semiLoserA = getLoserFromMatch(semiA);
  const semiLoserB = getLoserFromMatch(semiB);

  const repechageA = buildRepechageSide({
    side: "A",
    semiMatchIndex: 0,
    semiRoundIndex,
    mainRounds,
    combats,
  });

  const repechageB = buildRepechageSide({
    side: "B",
    semiMatchIndex: 1,
    semiRoundIndex,
    mainRounds,
    combats,
  });

  const bronzeA = createBronzeMatch({
    side: "A",
    c1: repechageA.qualifier,
    c2: semiLoserB || makePending("Perdant demi-finale opposée"),
    source1: repechageA.qualifierSourceKey,
    source2: null,
    combats,
  });

  const bronzeB = createBronzeMatch({
    side: "B",
    c1: repechageB.qualifier,
    c2: semiLoserA || makePending("Perdant demi-finale opposée"),
    source1: repechageB.qualifierSourceKey,
    source2: null,
    combats,
  });

  return {
    semiRoundIndex,
    semiA,
    semiB,
    semiLoserA,
    semiLoserB,
    repechageA,
    repechageB,
    bronzeA,
    bronzeB,
  };
};

/* -------------------------------------------------------------------------- */
/* Stats + podium                                                              */
/* -------------------------------------------------------------------------- */

const isPlayableMatch = (match) =>
  isRealCompetitor(match?.competiteur1) && isRealCompetitor(match?.competiteur2);

export const getSystematicRepechageStats = ({
  seed = [],
  mainRounds = [],
  repechageData,
}) => {
  const realCompetitorCount = seed.filter(isRealCompetitor).length;

  const mainMatches = mainRounds.flatMap((round) => round.pairs || []);

  const repMatches = [
    ...(repechageData?.repechageA?.matches || []),
    ...(repechageData?.repechageB?.matches || []),
    repechageData?.bronzeA,
    repechageData?.bronzeB,
  ].filter(Boolean);

  const allMatches = [...mainMatches, ...repMatches];

  const playable = allMatches.filter(isPlayableMatch);
  const completed = playable.filter((match) => !!match.combat);
  const toPlay = playable.filter((match) => !match.combat);

  return {
    baseSize: Math.max(2, seed.length),
    realCompetitorCount,
    completedCount: completed.length,
    toPlayCount: toPlay.length,
  };
};

export const getSystematicRepechagePodiumPreview = ({
  firstRoundSeed = [],
  competiteurs = [],
  combats = [],
  roundNames = [],
}) => {
  const mainRounds = buildMainRoundsForSystematicRepechage({
    seed: firstRoundSeed,
    competiteurs,
    combats,
    roundNames,
  });

  const repechageData = buildSystematicRepechageData({
    mainRounds,
    combats,
  });

  const finalRound = mainRounds[mainRounds.length - 1];
  const finalMatch = finalRound?.pairs?.[0] || null;

  let champion = null;
  let runnerUp = null;

  if (finalMatch?.combat) {
    const championId = getWinnerIdFromCombat(finalMatch.combat);
    const loser = getLoserFromMatch(finalMatch);

    champion = championId || null;
    runnerUp = getId(loser) || null;
  }

  const bronzeAId = repechageData.bronzeA?.combat
    ? getWinnerIdFromCombat(repechageData.bronzeA.combat)
    : null;

  const bronzeBId = repechageData.bronzeB?.combat
    ? getWinnerIdFromCombat(repechageData.bronzeB.combat)
    : null;

  return {
    champion,
    runnerUp,
    bronzes: [bronzeAId, bronzeBId].filter(Boolean),
    bronzeA: bronzeAId,
    bronzeB: bronzeBId,
    mainRounds,
    repechageData,
  };
};

export const computePodiumFromSystematicRepechage = ({
  firstRoundSeed = [],
  competiteurs = [],
  combats = [],
  roundNames = [],
}) => {
  const preview = getSystematicRepechagePodiumPreview({
    firstRoundSeed,
    competiteurs,
    combats,
    roundNames,
  });

  if (!preview.champion || !preview.runnerUp) return null;
  if (!preview.bronzeA || !preview.bronzeB) return null;

  return {
    champion: preview.champion,
    runnerUp: preview.runnerUp,
    bronzes: [preview.bronzeA, preview.bronzeB],
  };
};